// What happened around a session's flagged findings (lib/findingStory.js),
// for a reader meeting them for the first time: each finding and why its
// standard flagged it, the
// session's card with a pin where each moment happened, and one timeline of
// the moments in order: the
// ask, a failed call, a finding's messages, the reply. A long message shows
// the part a check quoted. A moment picked opens its message on the timeline
// and is ringed where it happened; pointed at, it previews its message and
// lights its row. On the card, any other mark opens its message too. The
// arrow keys step through every moment. Nothing here leaves the page, except
// the controls that open the session at the picked message.
import { useEffect, useMemo, useState } from "react";
import { turnMoment } from "../../lib/findingStory.js";
import { ACTION_CHIP } from "./triageParts.jsx";

const HIT = 18;
const DOT = " \u00b7 ";

const tint = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;
const toneOf = (f) => (f.kind === "finding" ? (f.met ? "var(--danger,#d4503e)" : "var(--warn,#c08a2d)") : f.kind === "failed" ? "var(--warn,#c08a2d)" : f.kind === "ask" ? "var(--ink)" : "var(--muted)");
const pinToneOf = (f) => (f.kind === "finding" ? (f.met ? "danger" : "warn") : f.kind === "failed" ? "warn" : "calm");
// Who a message is, as the record has it.
const WHO = {
  person: "The person",
  notice: "Notice",
  ran: "Agent ran",
  result: "Came back",
  failed: "Failed",
  said: "Agent said",
  thought: "Agent thought",
};
const PROSE = new Set(["person", "notice", "said", "thought"]);
// What a person wrote, as a chat bubble, so their words read apart from the
// agent's work.
const BUBBLE = "m-0 inline-block max-w-full min-w-0 justify-self-start rounded-[10px] rounded-bl-[3px] border border-[color:var(--line-strong)] bg-[color:var(--bg)] px-[9px] py-[2px] text-[12px] leading-[1.5] text-[color:var(--ink)]";
const WORK = new Set(["ran", "result", "failed"]);
const PEOPLE = new Set(["person", "notice"]);
// Past this many characters (or a second line) a message's line has more to
// show on request.
const LINE_CHARS = 120;
// The timeline: its column of times, the icon on its rail, and how far in
// from the row's start the rail runs.
const RAIL_TIME = 52;
const ICON_BOX = 20;
const RAIL_PAD = RAIL_TIME + ICON_BOX + 10;
const RAIL_AT = ICON_BOX / 2 + 6;
const CARD_WAIT_MS = 5000;
// The ring round a picked mark on the card that is no moment's (a moment's
// pin the card rings itself), and where a row's line stops under its pin.
const RING = 20;
const PIN_TIP = 7;
// A pause between two moments this long is said on the timeline.
export const GAP_MS = 10 * 60000;
const PREVIEW_W = 300;
const CELL_CONTEXT = "On the card";

const timeOf = (ms) => (Number.isFinite(ms) ? new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : null);
const pinTitle = (fi, mi) => `${fi + 1}.${mi + 1}`;
function spanText(ms) {
  const min = Math.round(ms / 60000);
  if (min < 1) return "under a minute";
  if (min < 60) return `${min} min`;
  const h = Math.round(min / 6) / 10;
  return `${Number.isInteger(h) ? h : h.toFixed(1)} h`;
}

// Who said or did a thing: a person, or the agent.
function WhoIcon({ who, color, size = 14 }) {
  const common = { width: size, height: size, viewBox: "0 0 12 12", fill: "none", stroke: "currentColor", strokeWidth: 1.2, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", className: "shrink-0", style: { color } };
  if (PEOPLE.has(who)) return <svg {...common} data-story-icon="person"><circle cx="6" cy="4" r="2" /><path d="M2.2 10.5c.6-2 2-3 3.8-3s3.2 1 3.8 3" /></svg>;
  return <svg {...common} data-story-icon="agent"><rect x="2" y="3.4" width="8" height="6.4" rx="1.4" /><path d="M6 1.4v2M4.4 6.4v.4M7.6 6.4v.4" /></svg>;
}
// The colour a moment's icon takes: the person's ink, a finding's act in its
// tone, a failure amber, the rest muted.
const iconTone = (flag, who) => (PEOPLE.has(who) ? "var(--ink)" : who === "failed" ? "var(--warn,#c08a2d)" : flag.kind === "finding" && who === "ran" ? toneOf(flag) : "var(--muted)");

// The card's pins for a story: one a moment, titled so the overlay finds it,
// on the row of the work for what the agent ran and on the conversation for
// what was said; the one titled `ringed` ringed.
export function storyPins(story, ringed = null) {
  return (story?.flags || []).flatMap((f, fi) => f.moments.map((m, mi) => {
    const title = pinTitle(fi, mi);
    return { turn: m.turn, tone: pinToneOf(f), count: 1, title, prefer: WORK.has(m.who) ? "work" : "talk", ...(title === ringed ? { ring: true } : {}) };
  }));
}

// A message's text with the part a check quoted marked.
function Marked({ text, mark }) {
  const at = mark ? text.indexOf(mark) : -1;
  if (at < 0) return text;
  return (
    <>
      {text.slice(0, at)}
      <mark className="rounded-[1px] px-[1px] text-[color:var(--ink)]" style={{ background: tint("var(--warn,#c08a2d)", 30) }} data-story-mark="">{mark}</mark>
      {text.slice(at + mark.length)}
    </>
  );
}

// One message: a line of it (the part a check quoted, where one did, else
// its start) until asked for all of it, the quoted part marked, and what
// came before and after it on request; who and when over it unless its row
// says so.
function Quote({ moment, tone, context = null, head = true, step = null }) {
  const [around, setAround] = useState(false);
  const [all, setAll] = useState(false);
  const prose = PROSE.has(moment.who);
  const line = moment.brief || moment.quote;
  const more = line !== moment.quote || moment.quote.length > LINE_CHARS || moment.quote.includes("\n");
  const link = "cursor-pointer bg-transparent p-0 text-[10px] leading-[1.4] text-[color:var(--muted)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]";
  return (
    <div data-story-card="" className="grid min-w-0 gap-[3px]">
      {head ? (
        <div className="flex items-center gap-x-[6px] text-[10px] leading-[1.4] text-[color:var(--muted)]">
          <span data-story-who={moment.who}>{WHO[moment.who] || WHO.said}</span>
          <span>{[context, timeOf(moment.at)].filter(Boolean).join(DOT)}</span>
          {step ? (
            <span className="ml-auto flex items-center gap-x-[4px] tabular-nums">
              <button type="button" aria-label="Previous message in this mark" onClick={step.prev} className={link}>{"\u2039"}</button>
              {step.label}
              <button type="button" aria-label="Next message in this mark" onClick={step.next} className={link}>{"\u203a"}</button>
            </span>
          ) : null}
        </div>
      ) : null}
      {moment.who === "person" ? (
        <p className={`${BUBBLE} ${all ? "whitespace-pre-wrap [overflow-wrap:anywhere]" : "truncate"}`} data-story-quote="" data-story-bubble="">
          <Marked text={all ? moment.quote : line} mark={moment.highlight} />
        </p>
      ) : (
        <p className={`m-0 pl-[8px] text-[color:var(--ink)] ${prose ? "text-[12px] leading-[1.5]" : "font-mono text-[11px] leading-[1.5]"} ${all ? "whitespace-pre-wrap [overflow-wrap:anywhere]" : "truncate"}`} style={{ borderLeft: `2px solid ${tone}` }} data-story-quote="">
          <Marked text={all ? moment.quote : line} mark={moment.highlight} />
        </p>
      )}
      <div className="flex flex-wrap items-center gap-x-[12px] pl-[10px]">
        {more ? <button type="button" onClick={() => setAll((v) => !v)} className={link} data-story-all="">{all ? "Show less" : "Show all"}</button> : null}
        {moment.before || moment.after ? <button type="button" onClick={() => setAround((v) => !v)} className={link} data-story-around="">Before and after</button> : null}
      </div>
      {around ? (
        <div className="grid gap-[1px] pl-[10px] font-mono text-[10px] leading-[1.5] text-[color:var(--muted-soft)]">
          {moment.before ? <span className="truncate" title={moment.before}>before: {moment.before}</span> : null}
          {moment.after ? <span className="truncate" title={moment.after}>after: {moment.after}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

// How long a pause was, in words.
const gapText = (ms) => `${spanText(ms)} later`;

// Every moment of the story in the order it happened, on a rail of times: an
// icon a message saying whether a person or the agent, the picked one quoted
// in place and ringed, the rest a line each (the part a check quoted, where
// one did), the one pointed at (here or where it happened) lit; a long pause
// between two said in words. A time shows where it changes.
function Timeline({ flags, steps, at, lit, onPick, onPoint }) {
  let last = null;
  let lastTime = null;
  return (
    <div className="relative grid" style={{ paddingLeft: RAIL_PAD }} data-story-timeline="">
      <span aria-hidden="true" className="absolute top-[12px] bottom-[12px] w-px bg-[color:var(--line-soft)]" style={{ left: RAIL_PAD - RAIL_AT }} />
      {steps.map((s, si) => {
        const f = flags[s.flag];
        const m = f.moments[s.moment];
        const on = si === at;
        const gap = last != null && Number.isFinite(m.at) && m.at - last >= GAP_MS ? gapText(m.at - last) : null;
        if (Number.isFinite(m.at)) last = m.at;
        const time = timeOf(m.at);
        const showTime = time && time !== lastTime;
        lastTime = time || lastTime;
        const color = iconTone(f, m.who);
        return (
          <div key={`${f.key}:${s.moment}`} className="grid">
            {gap ? <span className="py-[1px] text-[10px] leading-[1.4] text-[color:var(--muted-soft)]" data-story-gap="">{gap}</span> : null}
            <div className="relative min-w-0 py-[3px]" data-story-row={f.kind} onMouseEnter={() => onPoint(si)} onMouseLeave={() => onPoint(null)}>
              {lit === si ? <span aria-hidden="true" className="absolute inset-y-0 right-0 rounded-[6px] bg-[color:var(--control-bg)]" style={{ left: -RAIL_PAD }} data-story-lit="" /> : null}
              {showTime ? <span className="absolute top-[6px] whitespace-nowrap text-right text-[10px] leading-[1.4] tabular-nums text-[color:var(--muted)]" style={{ left: -RAIL_PAD, width: RAIL_TIME }}>{time}</span> : null}
              <span title={WHO[m.who]} className="absolute top-[3px] flex items-center justify-center rounded-full bg-[color:var(--panel)]" style={{ left: -(RAIL_AT + ICON_BOX / 2), width: ICON_BOX, height: ICON_BOX, boxShadow: on ? `0 0 0 1.5px ${toneOf(f)}` : undefined }} data-story-who={m.who}>
                <WhoIcon who={m.who} color={color} />
              </span>
              {on ? (
                <div className="relative grid">
                  <Quote key={`${f.key}:${s.moment}`} moment={m} tone={toneOf(f)} head={false} />
                </div>
              ) : (
                <button type="button" onClick={() => onPick(si)} title={WHO[m.who]} className="relative flex w-full min-w-0 cursor-pointer items-center bg-transparent py-0 text-left" data-story-row-pick={si}>
                  {m.who === "person"
                    ? <span className={`${BUBBLE} ml-[6px] truncate`} data-story-bubble="">{m.brief || m.quote}</span>
                    : <span className={`min-w-0 truncate pl-[10px] leading-[20px] text-[color:var(--ink)] ${PROSE.has(m.who) ? "text-[12px]" : "font-mono text-[11px]"}`}>{m.brief || m.quote}</span>}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// What a moment pointed at says, under it: who and when (and, for a
// finding's, the finding and the standard that flagged it), the message, and
// that a click opens it.
function Preview({ flag, moment, left, top }) {
  const finding = flag.kind === "finding";
  return (
    <div data-story-preview="" className="pointer-events-none absolute z-10 grid gap-[4px] rounded-[10px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] px-[9px] py-[7px] shadow-[0_6px_18px_rgba(0,0,0,0.12)]" style={{ left, top, width: PREVIEW_W }}>
      <div className="flex items-center gap-x-[6px] text-[10px] leading-[1.4]">
        <WhoIcon who={moment.who} color={iconTone(flag, moment.who)} />
        <span className="text-[color:var(--muted)]">{[WHO[moment.who], timeOf(moment.at)].filter(Boolean).join(DOT)}</span>
        {finding && flag.met && flag.standard ? <span className="ml-auto truncate text-[color:var(--ink)]">{`Flagged by ${flag.standard}`}</span> : null}
      </div>
      {finding ? <span className="text-[12px] font-medium leading-[1.4] text-[color:var(--ink)]">{flag.title}</span> : null}
      <span className={`line-clamp-4 whitespace-pre-wrap text-[color:var(--ink)] [overflow-wrap:anywhere] ${PROSE.has(moment.who) ? "text-[12px] leading-[1.45]" : "font-mono text-[11px] leading-[1.45]"}`}>{moment.brief || moment.quote}</span>
      <span className="text-[10px] leading-[1.4] text-[color:var(--muted-soft)]">Click to open it below</span>
    </div>
  );
}

// While the story loads (its page read, then the card laid out): a line
// with three dots pulsing in turn, for exactly as long as the work takes.
export function StoryLoading({ label = "Reading the trace" }) {
  return (
    <span aria-busy="true" role="status" data-story-loading="" className="inline-flex items-center gap-x-[3px] py-[2px] text-[11px] leading-[18px] text-[color:var(--muted)]">
      {label}
      {[0, 1, 2].map((i) => (
        <span key={i} aria-hidden="true" className="ml-[1px] h-[4px] w-[4px] rounded-full bg-[color:var(--muted)] animate-pulse motion-reduce:animate-none" style={{ animationDuration: "1s", animationDelay: `${i * 0.18}s` }} data-story-loading-dot="" />
      ))}
    </span>
  );
}

// `story`: findingStory's, over `turns`. `anchors`: where the card
// (`children`) has its pins, turns and cells, as the card reports them once
// it is laid out. `findingsFor(indexes, story)`: what the panel says about
// those findings, under each one's title. `summaryFor(story)`: what happened,
// once for them all, above them. `onRing(title | null)`: the pin of
// the moment picked, for the card to ring. `onOpen(moment)`: open the session
// at it.
export default function FindingStory({ story, turns, anchors, children, findingsFor = null, summaryFor = null, onRing = null, onOpen = null, timelineTitle = null }) {
  const flags = story?.flags || [];
  const steps = story?.steps || [];
  const findings = flags.filter((f) => f.kind === "finding");
  // The step picked: the finding's first message to begin with.
  const [at, setAt] = useState(() => {
    const i = steps.findIndex((s) => flags[s.flag]?.kind === "finding");
    return i >= 0 ? i : null;
  });
  // The step pointed at, where it happened or on its timeline row: { step, from }.
  const [hover, setHover] = useState(null);
  const point = (si, from) => setHover(si == null || si < 0 ? null : { step: si, from });
  // A mark on the card picked that is no moment's: the turns its cell stands
  // for, the one read, and where the cell is.
  const [cell, setCell] = useState(null);

  const layout = useMemo(() => {
    if (!story || !anchors?.width) return null;
    const byTitle = new Map(anchors.pins.map((p) => [p.title, p]));
    const xOf = (turn) => anchors.xOfTurn(turn) ?? anchors.body.left;
    // A moment whose cell holds another's pin shares that pin: the card
    // draws one pin a cell and row, titled for the first.
    const pins = flags.map((f, fi) => f.moments.map((m, mi) => {
      const own = byTitle.get(pinTitle(fi, mi));
      if (own) return own;
      const x = xOf(m.turn);
      const near = anchors.pins.reduce((best, p) => (Math.abs(p.x - x) < Math.abs((best?.x ?? Infinity) - x) ? p : best), null);
      return near && Math.abs(near.x - x) <= 8 ? { ...near, shared: true } : { x, y: null, shared: true };
    }));
    return { width: anchors.width, pins };
  }, [story, flags, anchors]);

  // A card that never says where its pins are still shows, pinless, rather
  // than leave the reader waiting.
  const [late, setLate] = useState(false);
  useEffect(() => {
    if (layout) return undefined;
    const t = setTimeout(() => setLate(true), CARD_WAIT_MS);
    return () => clearTimeout(t);
  }, [layout]);
  const cardShown = !!layout || late;
  const step = steps[at] || null;
  const ringed = step && !cell ? pinTitle(step.flag, step.moment) : null;
  useEffect(() => { onRing?.(ringed); }, [ringed, onRing]);
  const go = (i) => { setCell(null); setAt(i == null ? null : Math.max(0, Math.min(steps.length - 1, i))); };
  const stepBy = (d) => go(at == null ? (d > 0 ? 0 : steps.length - 1) : at + d);
  // A moment another finding's step stands for picks that step.
  const stepOf = (fi, mi) => {
    const own = steps.findIndex((s) => s.flag === fi && s.moment === mi);
    if (own >= 0) return own;
    const turn = flags[fi]?.moments[mi]?.turn;
    return steps.findIndex((s) => flags[s.flag].moments[s.moment].turn === turn);
  };
  const onKey = (e) => {
    if (e.key === "ArrowRight") { e.preventDefault(); stepBy(1); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); stepBy(-1); }
    else if (e.key === "Escape" && (at != null || cell)) { e.preventDefault(); go(null); }
  };
  // A click on the card: a moment's own mark picks it; any other mark opens
  // its message here.
  const onCard = (e) => {
    if (!anchors?.cellAt || !layout) return;
    const box = e.currentTarget.getBoundingClientRect();
    const hit = anchors.cellAt(e.clientX - box.left, e.clientY - box.top);
    if (!hit) { go(null); return; }
    const own = steps.findIndex((s) => hit.turns.includes(flags[s.flag].moments[s.moment].turn));
    if (own >= 0) { go(own); return; }
    setAt(null);
    setCell({ turns: hit.turns, k: 0, x: hit.x, y: hit.y });
  };
  const stepCell = (d) => setCell((cur) => (cur ? { ...cur, k: Math.max(0, Math.min(cur.turns.length - 1, cur.k + d)) } : cur));
  const cellMoment = cell ? turnMoment(turns, cell.turns[cell.k]) : null;
  // Where the workspace opens: the message shown, else the first flagged one.
  const shown = step || steps.find((x) => flags[x.flag]?.kind === "finding") || steps[0] || null;
  const opensAt = cellMoment || (shown ? flags[shown.flag].moments[shown.moment] : null);
  const hovered = hover ? steps[hover.step] : null;
  const hoverPin = hovered && layout ? layout.pins[hovered.flag][hovered.moment] : null;

  return (
    <div
      data-finding-story=""
      role="group"
      aria-label="What happened"
      tabIndex={0}
      onKeyDown={onKey}
      className="grid min-w-0 gap-[6px] outline-none"
    >
      {/* Until the card says where its pins are, the steps under way stand
          in for the story, which then shows whole. */}
      {cardShown ? null : <StoryLoading />}
      {/* Each finding and what the panel says about it; its title only
          where there are several, the row's head naming a lone one. Mounted
          while the card lays out, so what it asks for is already on its
          way, and shown with the card. A finding the panel has nothing
          more to say of, the summary having told it, is left off. */}
      {summaryFor ? <div className={cardShown ? "" : "hidden"} data-story-summary="">{summaryFor(story)}</div> : null}
      {findings.map((f) => {
        const said = findingsFor ? findingsFor([f.finding], story) : null;
        if (findingsFor && !said) return null;
        return (
          <div key={f.key} className={cardShown ? "grid gap-[3px]" : "hidden"} data-story-finding={f.finding}>
            {findings.length > 1 ? <span className="text-[12px] font-medium leading-[1.35] text-[color:var(--ink)]">{f.title || f.label}</span> : null}
            {said ? <div data-story-findings="">{said}</div> : null}
          </div>
        );
      })}
      {timelineTitle && cardShown ? <h3 className="mb-[4px] mt-[12px] text-[16px] font-medium text-[color:var(--ink)]">{timelineTitle}</h3> : null}
      {/* The card lays itself out unseen until it says where its pins
          are, so it never shows without them. */}
      <div className={`relative min-w-0 ${cardShown ? "" : "invisible h-0 overflow-hidden"}`}>
        {children}
        {layout ? <div aria-hidden="true" onClick={onCard} className="absolute inset-0 cursor-pointer" data-story-card-hit="" /> : null}
        {layout ? (
          <div className="pointer-events-none absolute inset-0" data-story-overlay="">
            {cell ? <span aria-hidden="true" className="absolute rounded-full" style={{ left: cell.x, top: cell.y, width: RING, height: RING, transform: "translate(-50%, -50%)", border: "1.5px solid var(--ink)" }} data-story-ring="" /> : null}
            {/* A timeline row pointed at: a line from under the card up
                to its pin, so the row finds its place. */}
            {hover?.from === "row" && hoverPin?.y != null ? (
              <svg className="absolute inset-0 h-full w-full overflow-visible" aria-hidden="true" data-story-link="">
                <line x1={hoverPin.x} x2={hoverPin.x} y1={hoverPin.y + PIN_TIP + 2} y2="100%" stroke={toneOf(flags[hovered.flag])} strokeWidth={1.25} strokeDasharray="3 2" />
              </svg>
            ) : null}
            {flags.map((f, fi) => f.moments.map((m, mi) => {
              const p = layout.pins[fi][mi];
              if (p.shared || p.y == null) return null;
              return (
                <button
                  key={`${f.key}:${mi}`}
                  type="button"
                  tabIndex={-1}
                  aria-hidden="true"
                  onClick={() => go(stepOf(fi, mi))}
                  onMouseEnter={() => point(stepOf(fi, mi), "card")}
                  onMouseLeave={() => point(null)}
                  className="pointer-events-auto absolute cursor-pointer rounded-full bg-transparent p-0"
                  style={{ left: p.x - HIT / 2, top: p.y - HIT / 2, width: HIT, height: HIT }}
                  data-story-pin={f.kind}
                />
              );
            }))}
            {hover?.from === "card" && hover.step !== at && hoverPin?.y != null ? (
              <Preview
                flag={flags[hovered.flag]}
                moment={flags[hovered.flag].moments[hovered.moment]}
                left={Math.max(0, Math.min(layout.width - PREVIEW_W, hoverPin.x - PREVIEW_W / 2))}
                top={hoverPin.y + HIT / 2 + 4}
              />
            ) : null}
          </div>
        ) : null}
        {/* The whole session in the workspace, at the message shown. */}
        {onOpen && opensAt?.messageId ? (
          <button type="button" data-story-open-workspace="" onClick={() => onOpen(opensAt)} className={`absolute top-[20px] right-[22px] ${ACTION_CHIP}`}>
            Open in investigation
          </button>
        ) : null}
      </div>
      <div data-story-panel="" className={`${cardShown ? "grid" : "hidden"} gap-[8px] rounded-[10px] border border-[color:var(--line)] bg-[color:var(--panel)] px-[9px] py-[7px]`}>
        {cellMoment ? (
          <div className="grid gap-[3px] rounded-[7px] border border-[color:var(--line-soft)] px-[7px] py-[5px]" data-story-cell="">
            <Quote
              key={`cell:${cell.turns[cell.k]}`}
              moment={cellMoment}
              tone="var(--line-strong)"
              context={CELL_CONTEXT}
              step={cell.turns.length > 1 ? { label: `${cell.k + 1} / ${cell.turns.length} in this mark`, prev: () => stepCell(-1), next: () => stepCell(1) } : null}
            />
          </div>
        ) : null}
        <Timeline flags={flags} steps={steps} at={cell ? null : at} lit={hover?.step ?? null} onPick={go} onPoint={(si) => point(si, "row")} />
      </div>
    </div>
  );
}
